export function createEventEmittingStorageRepositories(atomic, events) {
    const conversations = atomic.conversations === undefined
        ? {}
        : { conversations: createEventEmittingConversationRepository(atomic.conversations, events) };
    const notifications = atomic.notifications === undefined
        ? {}
        : { notifications: createEventEmittingNotificationRepository(atomic.notifications, events) };
    const agents = atomic.agents === undefined
        ? {}
        : { agents: createEventEmittingRevisionedRepository(atomic.agents, events) };
    const graphs = atomic.graphs === undefined
        ? {}
        : { graphs: createEventEmittingRevisionedRepository(atomic.graphs, events) };
    const revisionedSettings = atomic.revisionedSettings === undefined
        ? {}
        : { revisionedSettings: createEventEmittingRevisionedRepository(atomic.revisionedSettings, events) };
    const wallet = atomic.wallet === undefined
        ? {}
        : { wallet: createEventEmittingWalletRepository(atomic.wallet, events) };
    return {
        ...agents,
        ...conversations,
        events: atomic.events,
        ...graphs,
        ...notifications,
        outbox: createEventEmittingOutboxRepository(atomic.outbox, events),
        ...revisionedSettings,
        settings: createEventEmittingSettingsRepository(atomic.settings, events),
        syncLedger: createEventEmittingSyncLedgerRepository(atomic.syncLedger, events),
        ...wallet,
    };
}
function createEventEmittingWalletRepository(atomic, events) {
    return {
        readCommandResult: async (idempotencyKey) => await atomic.readCommandResult(idempotencyKey),
        readSnapshot: async () => await atomic.readSnapshot(),
        writeCommandResult: async (idempotencyKey, result) => await atomic.writeCommandResult(idempotencyKey, result),
        writeSnapshot: async (snapshot) => {
            await atomic.writeSnapshot(snapshot);
            await events.drain();
        },
    };
}
export function createEventEmittingRevisionedRepository(atomic, events) {
    return {
        apply: async (entity) => await writeAndDrain(() => atomic.apply(entity), events),
        command: async (input) => {
            const result = await atomic.command(input);
            await events.drain();
            return result;
        },
        list: async () => await atomic.list(),
        read: async (id) => await atomic.read(id),
    };
}
export function createEventEmittingConversationRepository(atomic, events) {
    return {
        acknowledgeMessage: async (input) => await writeAndDrain(() => atomic.acknowledgeMessage(input), events),
        appendPendingMessage: (input) => ({
            commit: async () => await writeAndDrain(() => atomic.appendPendingMessage(input).commit(), events),
        }),
        applyDelta: (delta) => ({
            commit: async () => await writeAndDrain(() => atomic.applyDelta(delta).commit(), events),
        }),
        failMessage: async (input) => await writeAndDrain(() => atomic.failMessage(input), events),
        listConversations: async (params) => await atomic.listConversations(params),
        listMessages: async (conversationId, params) => await atomic.listMessages(conversationId, params),
    };
}
export function createEventEmittingNotificationRepository(atomic, events) {
    return {
        applyDelta: (delta) => ({
            commit: async () => await writeAndDrain(() => atomic.applyDelta(delta).commit(), events),
        }),
        listNotifications: async (params) => await atomic.listNotifications(params),
        markRead: (input) => ({
            commit: async () => await writeAndDrain(() => atomic.markRead(input).commit(), events),
        }),
    };
}
export function createEventEmittingOutboxRepository(atomic, events) {
    const replay = atomic.claimHead === undefined || atomic.settle === undefined
        ? {}
        : {
            claimHead: async (input) => await atomic.claimHead(input),
            listStatus: async (filter) => {
                if (atomic.listStatus === undefined)
                    throw new Error("Outbox replay status is unavailable");
                return await atomic.listStatus(filter);
            },
            settle: async (input) => await writeAndDrain(() => atomic.settle(input), events),
        };
    return {
        create: async (input) => await writeAndDrain(() => atomic.create(input), events),
        delete: async (id) => await writeAndDrain(() => atomic.delete(id), events),
        list: async (filter) => await atomic.list(filter),
        read: async (id) => await atomic.read(id),
        updateState: async (id, state) => (await writeAndDrain(() => atomic.updateState(id, state), events)),
        ...replay,
    };
}
export function createEventEmittingSettingsRepository(atomic, events) {
    return {
        delete: async (key) => await writeAndDrain(() => atomic.delete(key), events),
        list: async () => await atomic.list(),
        read: async (key) => await atomic.read(key),
        set: async (input) => await writeAndDrain(() => atomic.set(input), events),
    };
}
export function createEventEmittingSyncLedgerRepository(atomic, events) {
    return {
        delete: async (domain) => await writeAndDrain(() => atomic.delete(domain), events),
        list: async () => await atomic.list(),
        read: async (domain) => await atomic.read(domain),
        setCursor: async (input) => await writeAndDrain(() => atomic.setCursor(input), events),
    };
}
async function writeAndDrain(write, events) {
    return await events.commitAndDrain(write);
}
//# sourceMappingURL=event-emitting-repository.js.map